import { Fragment, createElement, useMemo, type ReactNode } from 'react';
import type { Token, Tokens } from 'marked';
import { notificationLink, notificationTokens } from './notificationText';
import './notification-body.css';

function renderTokens(tokens: Token[], depth = 0): ReactNode {
  if (depth > 20) return tokens.map(token => token.raw).join('');
  return tokens.map((token, index) => {
    const nested = () => 'tokens' in token && token.tokens ? renderTokens(token.tokens, depth + 1) : String('text' in token ? token.text : '');
    let node: ReactNode;
    switch (token.type) {
      case 'space': case 'def': return null;
      case 'heading': node = createElement('h' + Math.min(6, Math.max(3, Number(token.depth) + 1)), {}, nested()); break;
      case 'paragraph': node = <p>{nested()}</p>; break;
      case 'text': node = nested(); break;
      case 'strong': node = <strong>{nested()}</strong>; break;
      case 'em': node = <em>{nested()}</em>; break;
      case 'del': node = <del>{nested()}</del>; break;
      case 'br': node = <br />; break;
      case 'hr': node = <hr />; break;
      case 'blockquote': node = <blockquote>{nested()}</blockquote>; break;
      case 'codespan': node = <code>{String(token.text)}</code>; break;
      case 'code': node = <pre><code>{String(token.text)}</code></pre>; break;
      case 'list': {
        const list = token as Tokens.List;
        const items = list.items.map((item, i) => <li key={i}>{item.task && <input type="checkbox" checked={!!item.checked} disabled aria-label={item.checked ? '已完成' : '未完成'} />}{renderTokens(item.tokens, depth + 1)}</li>);
        node = list.ordered ? <ol start={typeof list.start === 'number' ? list.start : undefined}>{items}</ol> : <ul>{items}</ul>;
        break;
      }
      case 'table': {
        const table = token as Tokens.Table;
        node = <div className="notice-document-table"><table><thead><tr>{table.header.map((cell, i) => <th key={i}>{renderTokens(cell.tokens, depth + 1)}</th>)}</tr></thead><tbody>{table.rows.map((row, i) => <tr key={i}>{row.map((cell, j) => <td key={j}>{renderTokens(cell.tokens, depth + 1)}</td>)}</tr>)}</tbody></table></div>;
        break;
      }
      case 'link': {
        const href = notificationLink(String(token.href));
        node = href ? <a href={href} target="_blank" rel="noopener noreferrer">{nested()}</a> : nested();
        break;
      }
      // Raw HTML and images remain inert text; do not load arbitrary remote assets.
      case 'image': node = String(token.text || ''); break;
      default: node = String(('text' in token && token.text) || token.raw || '');
    }
    return <Fragment key={index}>{node}</Fragment>;
  });
}
export function NotificationBody({ body, format = 'plain' }: { body: string; format?: 'plain' | 'markdown' }) {
  const content = useMemo(() => format === 'markdown'
    ? renderTokens(notificationTokens(body))
    : body.split(/\n\s*\n/).map((paragraph, index) => <p className="notice-document-plain" key={index}>{paragraph}</p>), [body, format]);
  return <div className="notice-document">{content}</div>;
}
